import React, { useEffect, useMemo, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header';
import { OutstandingPurchaseInvoice } from '@/app/types/payment';
import { PurchaseInvoiceAllocationFormValues } from '../schema';
import { UseFormReturn, useFieldArray } from 'react-hook-form';
import { PaymentOutFormValues } from '../schema';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

interface OutstandingPurchaseInvoicesTableProps {
  form: UseFormReturn<PaymentOutFormValues>;
  invoices: OutstandingPurchaseInvoice[];
  isLoading: boolean;
  totalPaymentAmount: number; // Pass the total payment amount entered by the user
}

// Helper function to format currency
const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return '₹0.00';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(Number(amount));
};

export function OutstandingPurchaseInvoicesTable({
  form,
  invoices,
  isLoading,
  totalPaymentAmount,
}: OutstandingPurchaseInvoicesTableProps): JSX.Element {
  const { control, setValue, watch, trigger } = form;
  const { fields, update } = useFieldArray({ control, name: 'allocations' });

  // Local state to manage total allocated amount display
  const [totalAllocatedDisplay, setTotalAllocatedDisplay] = useState(0);

  // Watch allocations to update total allocated display and remaining balance
  const allocations = watch('allocations');

  useEffect(() => {
      const currentTotalAllocated = allocations
          .filter(alloc => alloc.selected)
          .reduce((sum, alloc) => sum + (alloc.amount_to_allocate || 0), 0);
      setTotalAllocatedDisplay(currentTotalAllocated);
  }, [allocations]);

  const remainingBalance = totalPaymentAmount - totalAllocatedDisplay;


  // Update form allocations when invoices data changes (e.g., on party change)
  useEffect(() => {
    const newAllocations = invoices.map(inv => ({
        invoice_voucher_id: inv.id,
        invoice_number: inv.voucher_number,
        amount_due: Number(inv.amount_due ?? inv.total_amount), // Ensure amount_due is a number
        amount_to_allocate: 0, // Default allocation to 0
        selected: false, // Default to not selected
    }));
    setValue('allocations', newAllocations, { shouldValidate: false, shouldDirty: false });
  }, [invoices, setValue]);

  // --- Allocation Logic ---
  const handleAllocateMax = (index: number) => {
      const currentTotalAllocated = allocations
          .filter((alloc, idx) => alloc.selected && idx !== index) // Exclude current row from sum
          .reduce((sum, alloc) => sum + (alloc.amount_to_allocate || 0), 0);
      const availableToAllocate = totalPaymentAmount - currentTotalAllocated;
      const amountDue = allocations[index]?.amount_due ?? 0;
      const allocation = Math.max(0, Math.min(amountDue, availableToAllocate));

      update(index, { ...allocations[index], amount_to_allocate: allocation, selected: true });
      trigger(`allocations.${index}.amount_to_allocate`); // Trigger validation after update
  };

  const handleAutoAllocate = () => {
      let remainingToAllocate = totalPaymentAmount;
      const updatedAllocations = [...allocations]; // Create a mutable copy

      // Sort invoices by date ascending (oldest first) - assuming invoices are pre-sorted or sort here
      const sortedIndices = invoices
          .map((inv, index) => ({ ...inv, originalIndex: index }))
          .sort((a, b) => new Date(a.transaction_date).getTime() - new Date(b.transaction_date).getTime())
          .map(inv => inv.originalIndex);

      // Reset all allocations first
       updatedAllocations.forEach((_, index) => {
           updatedAllocations[index] = { ...updatedAllocations[index], amount_to_allocate: 0, selected: false };
       });


      for (const index of sortedIndices) {
          if (remainingToAllocate <= 0.001) break; // Stop if nothing left to allocate (use tolerance)

          const invoice = updatedAllocations[index];
          const amountDue = invoice.amount_due ?? 0;
          const canAllocate = Math.min(amountDue, remainingToAllocate);

          if (canAllocate > 0) {
              updatedAllocations[index] = {
                 ...invoice,
                 amount_to_allocate: parseFloat(canAllocate.toFixed(2)),
                 selected: true
              };
              remainingToAllocate -= canAllocate;
          }
      }

      setValue('allocations', updatedAllocations, { shouldValidate: true, shouldDirty: true });
      toast.success("Auto-allocated payment to oldest bills.");
  };


  // --- Column Definitions ---
  const columns = useMemo<ColumnDef<PurchaseInvoiceAllocationFormValues>[]>(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => {
              const isChecked = !!value;
              table.getRowModel().rows.forEach((row) => {
                const currentData = row.original;
                // Only select if there's an amount due
                if (currentData.amount_due > 0) {
                     update(row.index, { ...currentData, selected: isChecked });
                    // If selecting, potentially auto-fill allocation (optional)
                    // if (isChecked) handleAllocateMax(row.index);
                } else {
                     // Ensure rows with 0 due are not selected
                     update(row.index, { ...currentData, selected: false });
                }
              });
          }}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.original.selected}
          disabled={row.original.amount_due <= 0} // Disable if no amount due
          onCheckedChange={(value) => {
              const isChecked = !!value;
              update(row.index, { ...row.original, selected: isChecked });
              // Optionally zero out allocation if unchecked
              // if (!isChecked) update(row.index, { ...row.original, amount_to_allocate: 0, selected: false });
          }}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'invoice_number', // Use the field from PurchaseInvoiceAllocationFormValues
      header: ({ column }) => <DataTableColumnHeader column={column} title="Bill #" />,
      cell: ({ row }) => <div className="w-[100px]">{row.original.invoice_number}</div>,
    },
    {
        accessorFn: (row) => invoices.find(inv => inv.id === row.invoice_voucher_id)?.transaction_date,
        id: 'transaction_date',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Bill Date" />,
        cell: ({ row }) => {
            const dateStr = invoices.find(inv => inv.id === row.original.invoice_voucher_id)?.transaction_date;
            return <div className="w-[90px]">{dateStr ? format(new Date(dateStr), 'dd/MM/yyyy') : '-'}</div>;
        },
    },
    {
        accessorFn: (row) => invoices.find(inv => inv.id === row.invoice_voucher_id)?.total_amount,
        id: 'total_amount',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Bill Amount" />,
        cell: ({ row }) => {
            const totalAmount = invoices.find(inv => inv.id === row.original.invoice_voucher_id)?.total_amount;
            return <div className="w-[110px] text-right">{formatCurrency(totalAmount)}</div>;
        },
    },
    {
      accessorKey: 'amount_due',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Amount Due" />,
      cell: ({ row }) => <div className="w-[110px] text-right">{formatCurrency(row.original.amount_due)}</div>,
    },
    {
      accessorKey: 'amount_to_allocate',
      header: 'Allocate',
      cell: ({ row }) => (
        <div className="w-[150px] flex items-center gap-1">
           <Input
                type="number"
                step="0.01"
                className="h-8 text-right tabular-nums"
                placeholder='0.00'
                disabled={!row.original.selected || row.original.amount_due <= 0}
                value={row.original.amount_to_allocate?.toString() ?? ''}
                onChange={(e) => {
                    const value = e.target.value;
                    const numValue = value === '' ? 0 : parseFloat(value); // Treat empty as 0 for calc
                    if (!isNaN(numValue)) {
                       // Basic validation: cannot allocate more than amount due or more than remaining balance
                       const amountDue = row.original.amount_due ?? 0;
                       // const currentTotalAllocatedExcludingThis = totalAllocatedDisplay - (row.original.amount_to_allocate || 0);
                       // const maxAllocate = Math.max(0, Math.min(amountDue, totalPaymentAmount - currentTotalAllocatedExcludingThis));

                       // Simple check: cannot be negative or more than amount due
                       const clampedValue = Math.max(0, Math.min(amountDue, numValue));

                       update(row.index, { ...row.original, amount_to_allocate: clampedValue });
                       // Trigger validation after change if needed: trigger(`allocations.${row.index}.amount_to_allocate`);
                    } else {
                        // Handle invalid input (e.g., text) - maybe reset to 0 or previous value
                         update(row.index, { ...row.original, amount_to_allocate: 0 });
                    }
                }}
                onBlur={() => trigger(`allocations.${row.index}.amount_to_allocate`)} // Validate on blur
           />
            {/* Max Allocate Button */}
           <Button
               type="button"
               variant="outline"
               size="sm" // Use standard size
               className="h-8 px-2"
               onClick={() => handleAllocateMax(row.index)}
               disabled={!row.original.selected || row.original.amount_due <= 0}
               title="Allocate Max Possible"
           >
               Max
           </Button>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [invoices, update, handleAllocateMax, totalAllocatedDisplay, totalPaymentAmount]); // Add deps

  const table = useReactTable({
    data: fields, // Use fields from useFieldArray
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // manualPagination: true, // Not needed for this table
  });

  if (isLoading) {
    return <div className="text-center p-4">Loading bills...</div>;
  }

  if (!isLoading && invoices.length === 0) {
    return <div className="text-center p-4 text-muted-foreground">No outstanding bills found for the selected supplier.</div>;
  }

  return (
      <div className="space-y-3">
          <div className="flex justify-between items-center">
              <h4 className="font-medium">Allocate Payment to Bills</h4>
              <Button type="button" variant="outline" size="sm" onClick={handleAutoAllocate} disabled={totalPaymentAmount <= 0}>
                  Auto Allocate
              </Button>
          </div>
           <ScrollArea className="rounded-md border relative w-full">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id} style={{ width: header.getSize() }}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.original.selected && 'selected'}
                        className={cn(row.original.selected && 'bg-muted/50')}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} className="p-2">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        No bills loaded.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <ScrollBar orientation="horizontal" />
           </ScrollArea>
           {/* Summary Row */}
           <div className="flex justify-end space-x-6 text-sm font-medium pr-4">
               <span>Total Allocated: {formatCurrency(totalAllocatedDisplay)}</span>
               <span className={cn(remainingBalance < 0 ? 'text-destructive' : 'text-muted-foreground')}>
                    Remaining Balance: {formatCurrency(remainingBalance)}
               </span>
           </div>
      </div>
  );
}